// src/components/ContratoForm.jsx
import React, { useEffect, useState } from "react";
import {
  createContrato,
  updateContrato,
  getContratos,
  deleteContrato,
} from "../services/api";
import { useNavigate, useParams } from "react-router-dom";
import { OFICINAS_POR_MUNICIPIO } from "../catalogos/oficinasPorMunicipio";

// HeroIcons
import {
  MapPinIcon,
  UserIcon,
  BuildingOffice2Icon,
  EnvelopeIcon,
  PhoneIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  BuildingLibraryIcon,
  IdentificationIcon,
  LockClosedIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";

export default function ContratoForm() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [toasts, setToasts] = useState([]);
  const [contratosExistentes, setContratosExistentes] = useState([]);
  const [oficinasDisponibles, setOficinasDisponibles] = useState([]);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const municipios = Object.keys(OFICINAS_POR_MUNICIPIO);
  const COMPANIAS = ["Starlink", "Totalplay", "Telmex", "Izzi"];

  const [form, setForm] = useState({
    ubicacion: "",
    propietario: "",
    oficina: "",
    correo: "",
    telefono: "",
    fechaPago: "",
    totalPagar: "",
    compania: "",
    numeroCuenta: "",
    contrasena: "",
  });

  // ================================
  // TOASTS
  // ================================
  const showToast = (message, type = "success") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(
      () => setToasts((prev) => prev.filter((t) => t.id !== id)),
      3000
    );
  };

  const cerrarToast = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  // ================================
  // CARGAR CONTRATOS Y EDICIÓN
  // ================================
  useEffect(() => {
    const cargar = async () => {
      const res = await getContratos();
      const lista = res.data || [];
      setContratosExistentes(lista);

      if (id) {
        const found = lista.find((c) => String(c.id) === String(id));
        if (found) {
          setForm(found);
          const oficinas = OFICINAS_POR_MUNICIPIO[found.ubicacion] || [];
          setOficinasDisponibles(oficinas);
        }
      }
    };
    cargar();
  }, [id]);

  // ================================
  // HANDLERS
  // ================================
  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });
  };

  const handleUbicacionChange = (e) => {
    const ubicacion = e.target.value;
    const oficinas = OFICINAS_POR_MUNICIPIO[ubicacion] || [];
    setOficinasDisponibles(oficinas);

    setForm((prev) => ({
      ...prev,
      ubicacion,
      oficina: oficinas.length > 0 ? oficinas[0] : "",
    }));
  };

  // ================================
  // VALIDACIONES
  // ================================
  const validarFormulario = () => {
    const obligatorios = ["ubicacion", "propietario", "oficina", "telefono"];

    for (let campo of obligatorios) {
      if (!form[campo] || form[campo].trim() === "") {
        showToast(`El campo "${campo}" es obligatorio`, "error");
        return false;
      }
    }

    // Validar formato de correo (si viene)
    if (form.correo && form.correo.trim() !== "") {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(form.correo.trim())) {
        showToast("El correo no tiene un formato válido", "error");
        return false;
      }
    }

    // Validar teléfono: solo dígitos y longitud 8-12
    const telLimpio = form.telefono.replace(/\D/g, "");
    if (telLimpio.length < 8 || telLimpio.length > 12) {
      showToast(
        "El teléfono debe contener entre 8 y 12 dígitos numéricos",
        "error"
      );
      return false;
    }

    // Validar teléfonos duplicados
    const existeTelefono = contratosExistentes.some(
      (c) => c.telefono === form.telefono && String(c.id) !== String(id)
    );
    if (existeTelefono) {
      showToast("Este número de teléfono ya está registrado", "error");
      return false;
    }

    return true;
  };

  // ================================
  // GUARDAR (CONFIRMACIÓN + EJECUCIÓN)
  // ================================
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validarFormulario()) return;
    setShowConfirm(true);
  };

  const ejecutarGuardado = async () => {
    try {
      setIsSaving(true);

      if (id) {
        await updateContrato(id, form);
        showToast("Contrato editado correctamente", "success");
        navigate("/contratos");
      } else {
        await createContrato(form);
        showToast("Contrato agregado correctamente", "success");
        // Limpiar todo
        setForm({
          ubicacion: "",
          propietario: "",
          oficina: "",
          correo: "",
          telefono: "",
          fechaPago: "",
          totalPagar: "",
          compania: "",
          numeroCuenta: "",
          contrasena: "",
        });
        setOficinasDisponibles([]);
      }
    } catch (error) {
      console.error("❌ ERROR:", error);
      showToast("Ocurrió un error al guardar", "error");
    } finally {
      setIsSaving(false);
      setShowConfirm(false);
    }
  };

  // ================================
  // ELIMINAR CONTRATO
  // ================================
  const handleDelete = async () => {
    if (!id) return;
    const confirmar = window.confirm(
      "¿Seguro que deseas eliminar este contrato? Esta acción no se puede deshacer."
    );
    if (!confirmar) return;

    try {
      await deleteContrato(id);
      showToast("Contrato eliminado correctamente", "success");
      navigate("/contratos");
    } catch (error) {
      console.error("❌ ERROR ELIMINANDO:", error);
      showToast("Ocurrió un error al eliminar", "error");
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-200 to-slate-300 flex items-center justify-center p-4 md:p-8">
      {/* TOASTS */}
      <div className="toast-container">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.type}`}>
            <span>{t.message}</span>
            <span className="toast-close" onClick={() => cerrarToast(t.id)}>
              ×
            </span>
          </div>
        ))}
      </div>

      {/* CARD PRINCIPAL */}
      <div className="bg-white rounded-2xl shadow-2xl p-6 md:p-10 max-w-5xl w-full border border-slate-200">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h2 className="text-3xl font-bold text-slate-800">
              {id ? "Editar Contrato" : "Agregar Contrato"}
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Captura la información del contrato de servicio de internet /
              telefonía.
            </p>
          </div>

          {id && (
            <button
              type="button"
              onClick={handleDelete}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-red-200 text-red-600 text-sm font-semibold hover:bg-red-50 transition"
            >
              <TrashIcon className="w-5 h-5" />
              Eliminar contrato
            </button>
          )}
        </div>

        <form
          onSubmit={handleSubmit}
          className="space-y-8"
        >
          {/* SECCIÓN: DATOS GENERALES */}
          <section>
            <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-700 mb-4">
              <BuildingLibraryIcon className="w-5 h-5 text-blue-500" />
              Datos generales
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 p-4 rounded-xl border border-slate-100">
              {/* UBICACIÓN */}
              <div>
                <label className="label flex items-center gap-2">
                  <MapPinIcon className="w-5 h-5 text-blue-500" />
                  Ubicación *
                </label>
                <select
                  name="ubicacion"
                  value={form.ubicacion}
                  onChange={handleUbicacionChange}
                  className="input"
                >
                  <option value="">Seleccione ubicación</option>
                  {municipios.map((m) => (
                    <option key={m} value={m}>
                      {m}
                    </option>
                  ))}
                </select>
              </div>

              {/* PROPIETARIO */}
              <div>
                <label className="label flex items-center gap-2">
                  <UserIcon className="w-5 h-5 text-blue-500" />
                  Propietario *
                </label>
                <input
                  name="propietario"
                  value={form.propietario}
                  onChange={handleChange}
                  className="input"
                />
              </div>

              {/* OFICINA */}
              <div>
                <label className="label flex items-center gap-2">
                  <BuildingOffice2Icon className="w-5 h-5 text-blue-500" />
                  Oficina *
                </label>
                <select
                  name="oficina"
                  value={form.oficina}
                  onChange={handleChange}
                  className="input"
                >
                  <option value="">Seleccione oficina</option>
                  {oficinasDisponibles.map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </div>

              {/* CORREO */}
              <div>
                <label className="label flex items-center gap-2">
                  <EnvelopeIcon className="w-5 h-5 text-blue-500" />
                  Correo
                </label>
                <input
                  name="correo"
                  type="email"
                  value={form.correo}
                  onChange={handleChange}
                  className="input"
                />
              </div>

              {/* TELÉFONO */}
              <div>
                <label className="label flex items-center gap-2">
                  <PhoneIcon className="w-5 h-5 text-blue-500" />
                  Teléfono *
                </label>
                <input
                  name="telefono"
                  value={form.telefono}
                  onChange={handleChange}
                  className="input"
                />
              </div>
            </div>
          </section>

          {/* SECCIÓN: DETALLES DEL CONTRATO */}
          <section>
            <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-700 mb-4">
              <IdentificationIcon className="w-5 h-5 text-blue-500" />
              Detalles del contrato
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-slate-50 p-4 rounded-xl border border-slate-100">
              {/* FECHA */}
              <div>
                <label className="label flex items-center gap-2">
                  <CalendarDaysIcon className="w-5 h-5 text-blue-500" />
                  Fecha de pago
                </label>
                <input
                  type="date"
                  name="fechaPago"
                  value={form.fechaPago}
                  onChange={handleChange}
                  className="input"
                />
              </div>

              {/* TOTAL */}
              <div>
                <label className="label flex items-center gap-2">
                  <CurrencyDollarIcon className="w-5 h-5 text-blue-500" />
                  Total a pagar
                </label>
                <input
                  name="totalPagar"
                  value={form.totalPagar}
                  onChange={handleChange}
                  className="input"
                  placeholder="Ej. 1,200.00"
                />
              </div>

              {/* COMPAÑÍA */}
              <div>
                <label className="label flex items-center gap-2">
                  <BuildingOffice2Icon className="w-5 h-5 text-blue-500" />
                  Compañía
                </label>
                <select
                  name="compania"
                  value={form.compania}
                  onChange={handleChange}
                  className="input"
                >
                  <option value="">Seleccione compañía</option>
                  {COMPANIAS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </section>

          {/* SECCIÓN: ACCESO / CUENTA */}
          <section>
            <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-700 mb-4">
              <LockClosedIcon className="w-5 h-5 text-blue-500" />
              Acceso / cuenta
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 p-4 rounded-xl border border-slate-100">
              {/* NUMERO CUENTA */}
              <div>
                <label className="label flex items-center gap-2">
                  <IdentificationIcon className="w-5 h-5 text-blue-500" />
                  Número de cuenta
                </label>
                <input
                  name="numeroCuenta"
                  value={form.numeroCuenta}
                  onChange={handleChange}
                  className="input"
                />
              </div>

              {/* CONTRASEÑA */}
              <div>
                <label className="label flex items-center gap-2">
                  <LockClosedIcon className="w-5 h-5 text-blue-500" />
                  Contraseña
                </label>
                <input
                  name="contrasena"
                  value={form.contrasena}
                  onChange={handleChange}
                  className="input"
                />
              </div>
            </div>
          </section>

          {/* BOTONES */}
          <div className="flex flex-col md:flex-row gap-3 mt-2">
            <button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-xl transition text-center"
              disabled={isSaving}
            >
              {isSaving
                ? "Guardando..."
                : id
                ? "Guardar cambios"
                : "Guardar contrato"}
            </button>

            <button
              type="button"
              onClick={() => navigate("/contratos")}
              className="flex-1 border border-slate-300 text-slate-700 font-semibold px-8 py-3 rounded-xl hover:bg-slate-50 transition text-center"
            >
              Cancelar
            </button>
          </div>
        </form>
      </div>

      {/* MODAL CONFIRMACIÓN GUARDAR */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[99998]">
          <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md border border-slate-200">
            <h3 className="text-xl font-semibold text-slate-800 mb-2">
              Confirmar guardado
            </h3>
            <p className="text-sm text-slate-600 mb-6">
              ¿Confirmas que deseas guardar este contrato con la información
              capturada?
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 rounded-xl border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={ejecutarGuardado}
                className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
                disabled={isSaving}
              >
                {isSaving ? "Guardando..." : "Confirmar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ESTILOS EXTRA (TOASTS + INPUT BASE) */
const style = `
.label {
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
}
.input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 0.75rem;
  margin-top: 4px;
  font-size: 0.95rem;
}
.input:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 2px #bfdbfe;
}

.toast-container {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 99999;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.toast {
  min-width: 260px;
  padding: 14px 20px;
  border-radius: 14px;
  font-size: 1rem;
  font-weight: 600;
  color: white;
  animation: slideIn 0.35s ease-out;
}
@keyframes slideIn {
  from { opacity: 0; transform: translateX(40px); }
  to { opacity: 1; transform: translateX(0); }
}
.toast-success { background-color: #16a34a; }
.toast-error { background-color: #dc2626; }
.toast-warning { background-color: #f59e0b; }
.toast-info { background-color: #2563eb; }
.toast-close {
  margin-left: 10px;
  cursor: pointer;
  font-size: 20px;
  opacity: 0.8;
}
.toast-close:hover { opacity: 1; }
`;
document.head.insertAdjacentHTML("beforeend", `<style>${style}</style>`);
